import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';
import '../styles/Layout.css';

// Icons
const HomeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PatientIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 13C7.58172 13 4 16.5817 4 21H20C20 16.5817 16.4183 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 3V7C14 7.55228 14.4477 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H14L19 8V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 13H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 17H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DoctorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 11C13.6569 11 15 9.65685 15 8C15 6.34315 13.6569 5 12 5C10.3431 5 9 6.34315 9 8C9 9.65685 10.3431 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 16H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 11V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 13V17M12 9V17M16 5V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { invalidate } = useCache();
  const [minimized, setMinimized] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<string>('');
  


  const handleLogout = async () => {
    await logout();
    // Clear all caches when logging out
    invalidate.all();
    navigate('/login');
  };

  const handleGoBack = () => {
    navigate(-1); // Navigate to the previous page in history
  };

  if (!isAuthenticated || !user) {
    return <>{children}</>;
  }

  // Check if the current path matches the link
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Get role badge color
  const getRoleBadgeColor = (role: string) => {
    switch(role) {
      case 'provider': return 'role-provider';
      case 'patient': return 'role-patient';
      case 'superuser': return 'role-admin';
      default: return '';
    }
  };
  
  // Get initials for avatar
  const getInitials = () => {
    if (!user) return 'U';
    
    // If user has first name and last name, use those
    if (user.fname && user.lname) {
      return `${user.fname[0]}${user.lname[0]}`.toUpperCase();
    }
    
    // If user has only first name
    if (user.fname && !user.lname) {
      return user.fname[0].toUpperCase();
    }
    
    // Fallback to username
    if (user.username) {
      // If username contains a space, use first letters of each part
      const nameParts = user.username.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return user.username[0].toUpperCase();
    }
    
    return 'U';
  };

  return (
    <div className="layout">
      <div className={`sidebar ${minimized ? 'minimized' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="logo-icon">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <rect x="10" y="6" width="4" height="12" rx="2" fill="currentColor" />
              <rect x="6" y="10" width="12" height="4" rx="2" fill="currentColor" />
            </svg>
            <span className="sidebar-title">Neutrino</span>
          </div>
          <button
            className="minimize-btn"
            onClick={() => setMinimized(!minimized)}
            title={minimized ? "Expand sidebar" : "Minimize sidebar"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {minimized ? (
                // Right arrow icon when minimized
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              ) : (
                // Left arrow icon when expanded
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
        </div>
        
        {user && (
          <div className="user-profile">
            <div className="user-avatar">{getInitials()}</div>
            <div className="user-info">
              <div className="user-name">{user.fname ? `${user.fname} ${user.lname || ''}` : user.username}</div>
              <div className="user-role">
                <span className={`role-badge ${getRoleBadgeColor(user.role)}`}>
                  {user.role}
                </span>
                <span className="time-display"></span>
              </div>
            </div>
          </div>
        )}
        
        <Link to="/dashboard" className={isActive('/dashboard') ? 'active' : ''} title="Dashboard">
          <HomeIcon /> <span className="link-text">Dashboard</span>
        </Link>

        {user.role === 'patient' && (
          <>
            <Link to={`/patients/${user.id}`} className={isActive(`/patients/${user.id}`) ? 'active' : ''} title="My Medical Records">
              <PatientIcon /> <span className="link-text">My Medical Records</span>
            </Link>
            <Link to={`/prior-authorizations/${user.id}`} className={isActive(`/prior-authorizations/${user.id}`) ? 'active' : ''} title="My Prior Authorizations">
              <DocumentIcon /> <span className="link-text">My Prior Authorizations</span>
            </Link>
          </>
          )}

        {user.role === 'provider' && (
          <>
            <Link to="/patients" className={isActive('/patients') ? 'active' : ''} title="Patient List">
              <PatientIcon /> <span className="link-text">Patient List</span>
            </Link>
            <Link to="/requests" className={isActive('/requests') ? 'active' : ''} title="Requests">
              <PatientIcon /> <span className="link-text">Requests</span>
            </Link>
            <Link to="/provider/pa-status" className={isActive('/provider/pa-status') ? 'active' : ''} title="Prior Authorizations">
              <DocumentIcon /> <span className="link-text">Prior Authorizations</span>
            </Link>
          </>
          )}

        {user.role === 'superuser' && (
          <>
            <Link to="/patients" className={isActive('/patients') ? 'active' : ''} title="Patients">
              <PatientIcon /> <span className="link-text">Patients</span>
            </Link>
            <Link to="/practitioners" className={isActive('/practitioners') ? 'active' : ''} title="Providers">
              <DoctorIcon /> <span className="link-text">Providers</span>
            </Link>
            <Link to="/prior-authorizations" className={isActive('/prior-authorizations') ? 'active' : ''} title="Prior Authorizations">
              <DocumentIcon /> <span className="link-text">Prior Authorizations</span>
            </Link>
            <Link to="/admin-overview" className={isActive('/admin-overview') ? 'active' : ''} title="System Overview">
              <ChartIcon /> <span className="link-text">System Overview</span>
            </Link>
          </>
        )}
        
        {user.role === 'payer' && (
          <>
            <Link to="/payer/pending-authorizations" className={isActive('/payer/pending-authorizations') ? 'active' : ''} title="Pending Authorizations">
              <DocumentIcon /> <span className="link-text">Pending Authorizations</span>
            </Link>
            <Link to="/payer/reviewed-authorizations" className={isActive('/payer/reviewed-authorizations') ? 'active' : ''} title="Reviewed Authorizations">
              <DocumentIcon /> <span className="link-text">Reviewed Authorizations</span>
            </Link>
            <Link to="/payer/epa-management" className={isActive('/payer/epa-management') ? 'active' : ''} title="ePA Management">
              <ChartIcon /> <span className="link-text">ePA Management</span>
            </Link>
          </>
        )}
        
        <div className="logout-link">
          <button onClick={handleLogout} title="Logout">
            <LogoutIcon /> <span className="link-text">Logout</span>
          </button>
        </div>
      </div>

      <div className={`main-content ${minimized ? 'expanded' : ''}`}>
        <div className="main-header">
          <div className="back-button-container">
            <button className="back-button" onClick={handleGoBack} title="Go back to previous page">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Back</span>
            </button>
          </div>
          <div className="page-info">
            <h1 className="page-title">{location.pathname.split('/').pop()?.replace(/-/g, ' ') || 'Dashboard'}</h1>
          </div>
        </div>
        <div className="content-wrapper">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;

